class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    // Delay setting up cart icon accessibility until DOM is ready
    // Header renders after cart drawer, so we need to wait
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setHeaderCartIconAccessibility());
    } else {
      // DOM is already ready, but retry a few times to ensure header is rendered
      this.setHeaderCartIconAccessibility();
      setTimeout(() => this.setHeaderCartIconAccessibility(), 100);
      setTimeout(() => this.setHeaderCartIconAccessibility(), 500);
    }
    
    // Swipe state (persisted across re-renders)
    this.swipeState = {
      startY: 0,
      currentY: 0,
      isDragging: false,
      isDraggingStarted: false,
      rafId: null,
      threshold: 100
    };
    
    // Initialize swipe to close
    this.initSwipeToClose();
  }

  initSwipeToClose() {
    // Use event delegation on the cart-drawer element itself
    // This survives re-renders of the inner content
    
    // Touch events for mobile
    this.addEventListener('touchstart', (e) => {
      // Only trigger if touching the header
      if (!e.target.closest('.drawer__header')) return;
      
      this.swipeState.startY = e.touches[0].clientY;
      this.swipeState.isDragging = true;
      this.swipeState.isDraggingStarted = true;
      
      // Disable transition during drag for performance
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.style.transition = 'none';
        drawerInner.style.willChange = 'transform';
      }
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (!this.swipeState.isDragging) return;
      
      // Prevent Safari rubber-band scrolling when dragging down
      if (this.swipeState.isDraggingStarted) {
        const diff = e.touches[0].clientY - this.swipeState.startY;
        if (diff > 0) {
          e.preventDefault();
        }
      }
      
      this.swipeState.currentY = e.touches[0].clientY;
      const diff = this.swipeState.currentY - this.swipeState.startY;
      const speedMultiplier = 1.5;
      
      // Use requestAnimationFrame for smooth Safari performance
      if (!this.swipeState.rafId) {
        this.swipeState.rafId = requestAnimationFrame(() => {
          const drawerInner = this.querySelector('.drawer__inner');
          if (drawerInner) {
            if (diff > 0) {
              drawerInner.style.transform = `translateY(${diff * speedMultiplier}px)`;
            } else {
              drawerInner.style.transform = 'translateY(0)';
            }
          }
          this.swipeState.rafId = null;
        });
      }
    }, { passive: false });

    this.addEventListener('touchend', () => {
      if (!this.swipeState.isDragging) return;
      this.swipeState.isDragging = false;
      this.swipeState.isDraggingStarted = false;
      
      // Cancel any pending animation frame
      if (this.swipeState.rafId) {
        cancelAnimationFrame(this.swipeState.rafId);
        this.swipeState.rafId = null;
      }
      
      const diff = this.swipeState.currentY - this.swipeState.startY;
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.style.willChange = 'auto';
        drawerInner.style.transition = 'transform 0.3s ease';
        
        if (diff > this.swipeState.threshold) {
          this.close();
        } else {
          drawerInner.style.transform = 'translateY(0)';
        }
      }
    });

    // Mouse events for desktop
    this.addEventListener('mousedown', (e) => {
      if (!e.target.closest('.drawer__header')) return;
      
      this.swipeState.startY = e.clientY;
      this.swipeState.isDragging = true;
      
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.style.transition = 'none';
        drawerInner.style.willChange = 'transform';
      }
    });

    this.addEventListener('mousemove', (e) => {
      if (!this.swipeState.isDragging) return;
      
      this.swipeState.currentY = e.clientY;
      const diff = this.swipeState.currentY - this.swipeState.startY;
      const speedMultiplier = 1.5;
      
      if (!this.swipeState.rafId) {
        this.swipeState.rafId = requestAnimationFrame(() => {
          const drawerInner = this.querySelector('.drawer__inner');
          if (drawerInner) {
            if (diff > 0) {
              drawerInner.style.transform = `translateY(${diff * speedMultiplier}px)`;
            } else {
              drawerInner.style.transform = 'translateY(0)';
            }
          }
          this.swipeState.rafId = null;
        });
      }
    });

    this.addEventListener('mouseup', () => {
      if (!this.swipeState.isDragging) return;
      this.swipeState.isDragging = false;
      
      if (this.swipeState.rafId) {
        cancelAnimationFrame(this.swipeState.rafId);
        this.swipeState.rafId = null;
      }
      
      const diff = this.swipeState.currentY - this.swipeState.startY;
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.style.willChange = 'auto';
        drawerInner.style.transition = 'transform 0.3s ease';
        
        if (diff > this.swipeState.threshold) {
          this.close();
        } else {
          drawerInner.style.transform = 'translateY(0)';
        }
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Remove closing class if present
    this.classList.remove('closing');
    
    // Reset transform
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.style.transform = '';
    }
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    
    // Update cart badge when drawer opens
    this.updateCartBadge();

    // Lock body scroll (save position for iOS Safari)
    this.bodyScrollLock = {
      scrollY: window.scrollY
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.bodyScrollLock.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Add closing class for animation
    this.classList.add('closing');
    
    // Reset any inline transform
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.style.transform = '';
    }
    
    // Wait for animation to complete before removing active class
    setTimeout(() => {
      this.classList.remove('active', 'closing');
      removeTrapFocus(this.activeElement);
      
      // Restore body scroll
      document.body.classList.remove('overflow-hidden');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      if (this.bodyScrollLock) {
        window.scrollTo(0, this.bodyScrollLock.scrollY);
        this.bodyScrollLock = null;
      }
    }, 300); // Match CSS transition duration
  }

  // Update cart badge by fetching cart data directly
  // Badge always exists in DOM - just toggle visibility (prevents flash)
  updateCartBadge() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const bubbleElement = document.getElementById('cart-icon-bubble');
        if (!bubbleElement) return;
        
        const currentBadge = bubbleElement.querySelector('.cart-count-badge');
        if (!currentBadge) return;
        
        // Update count first
        const countSpan = currentBadge.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = cart.item_count < 100 ? cart.item_count : '99+';
        }
        
        // Toggle visibility (badge always in DOM)
        if (cart.item_count > 0) {
          currentBadge.classList.remove('cart-count-badge--hidden');
        } else {
          currentBadge.classList.add('cart-count-badge--hidden');
        }
        
        // Also update ATC button quantity on product page
        this.updateATCButtonQuantity(cart.item_count);
      })
      .catch(error => console.error('Error updating cart badge:', error));
  }
  
  // Update ATC button quantity badge on product page
  updateATCButtonQuantity(itemCount) {
    const atcQtyBadge = document.querySelector('.product-sticky-atc__qty');
    if (!atcQtyBadge) return;
    
    if (itemCount > 0) {
      atcQtyBadge.textContent = itemCount < 100 ? itemCount : '99+';
      atcQtyBadge.style.display = 'flex';
    } else {
      atcQtyBadge.style.display = 'none';
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      
      // Special handling for cart-icon-bubble: only update badge count
      // Badge always in DOM - just toggle visibility
      if (section.id === 'cart-icon-bubble') {
        const newHTML = parsedState.sections[section.id];
        if (!newHTML) return;
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(newHTML, 'text/html');
        const newBadge = doc.querySelector('.cart-count-badge');
        const currentBadge = sectionElement.querySelector('.cart-count-badge');
        
        if (newBadge && currentBadge) {
          // Update count and ensure visible
          const newCount = newBadge.querySelector('span[aria-hidden="true"]');
          const currentCount = currentBadge.querySelector('span[aria-hidden="true"]');
          if (newCount && currentCount) {
            currentCount.textContent = newCount.textContent;
          }
          currentBadge.classList.remove('cart-count-badge--hidden');
        } else if (currentBadge) {
          // Hide badge if cart empty
          currentBadge.classList.add('cart-count-badge--hidden');
        }
      } else {
        // Normal section update
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
